import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import NurtureDashboard from '../components/NurtureDashboard';
import SystemAuditPanel from '../components/SystemAuditPanel';
import {
  TrendUp,
  CurrencyCircleDollar,
  Users,
  ChartLine,
  Target,
  Trophy,
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `/api`;

const Diretoria = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  // Calculate estimated revenue (mock calculation)
  const avgTicket = 350000; // R$ 350k average ticket
  const estimatedRevenue = (stats?.qualified_leads || 0) * avgTicket * 0.15; // 15% conversion
  const cac = 450; // Mock CAC value

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="diretoria-title">
              Painel da Diretoria
            </h1>
            <p className="text-slate-600 mt-1">Métricas estratégicas e performance comercial</p>
          </div>

          <a
            href="/performance-comercial"
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition whitespace-nowrap"
          >
            Performance Comercial →
          </a>
        </div>

        <SystemAuditPanel />

        {/* Executive KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
                <CurrencyCircleDollar size={24} weight="duotone" />
              </div>
              <TrendUp size={20} />
            </div>
            <p className="text-3xl font-bold mb-1">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                estimatedRevenue
              )}
            </p>
            <p className="text-emerald-100">Receita Potencial</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
                <Target size={24} weight="duotone" />
              </div>
              <TrendUp size={20} />
            </div>
            <p className="text-3xl font-bold mb-1">{stats?.conversion_rate || 0}%</p>
            <p className="text-blue-100">Taxa de Conversão</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
                <ChartLine size={24} weight="duotone" />
              </div>
            </div>
            <p className="text-3xl font-bold mb-1">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cac)}
            </p>
            <p className="text-purple-100">CAC Estimado</p>
          </div>
        </div>

        {/* Detailed Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance by Broker */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-5">
              <Trophy size={24} weight="duotone" className="text-blue-900" />
              <h3 className="text-lg font-semibold text-slate-900">Performance de Corretores</h3>
            </div>
            {stats?.broker_ranking && stats.broker_ranking.length > 0 ? (
              <div className="space-y-3">
                {stats.broker_ranking.map((broker, index) => (
                  <div key={broker._id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0
                            ? 'bg-yellow-100 text-yellow-700'
                            : index === 1
                            ? 'bg-slate-200 text-slate-700'
                            : index === 2
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {index + 1}º
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{broker.name}</p>
                        <p className="text-xs text-slate-500">{broker.count} leads atribuídos</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-emerald-600">12% conversão</p>
                      <p className="text-xs text-slate-500">R$ 420k pipeline</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nenhum dado disponível</p>
            )}
          </div>

          {/* Channel Performance */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-5">
              <ChartLine size={24} weight="duotone" className="text-blue-900" />
              <h3 className="text-lg font-semibold text-slate-900">Performance por Canal</h3>
            </div>
            {stats?.source_distribution && stats.source_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.source_distribution.map((s) => ({ name: s._id, value: s.count }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="value" fill="#10B981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-500 text-center py-8">Nenhum dado disponível</p>
            )}
          </div>
        </div>

        {/* Nurture Sequences Dashboard */}
        <NurtureDashboard />

        {/* Key Insights */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Insights Estratégicos</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm font-medium text-emerald-900 mb-2">✓ Performance Positiva</p>
              <p className="text-sm text-emerald-700">
                Taxa de conversão 15% acima da média do mercado. Continue investindo em qualificação por IA.
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900 mb-2">⚡ Oportunidade</p>
              <p className="text-sm text-blue-700">
                Leads do Facebook Ads apresentam melhor score. Considere aumentar investimento neste canal.
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm font-medium text-orange-900 mb-2">⚠ Atenção</p>
              <p className="text-sm text-orange-700">
                Tempo médio de resposta pode ser reduzido. IA está acelerando primeiras interações em 85%.
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm font-medium text-purple-900 mb-2">📊 Meta do Mês</p>
              <p className="text-sm text-purple-700">
                Faltam apenas 8 leads qualificados para bater a meta de 50 leads quentes do mês.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Diretoria;